import { useState } from 'react';
import { Dialog, api, useToast, AEA_BROKERS, AEA_REQUEST_TYPES, aeaDsarLetter } from './_barrel.js';


        // Log a data-subject request to a broker for one device, and hand the
        // analyst a ready-to-send letter to email out. Records the request so the
        // tab can track the statutory deadline and later attach the response.
        function AeaBrokerRequestDialog({ deviceId, subjectName, maidTail, token, onClose, onDone }) {
            const toast = useToast();
            const [brokerIdx, setBrokerIdx] = useState(0);
            const [customName, setCustomName] = useState('');
            const [customContact, setCustomContact] = useState('');
            const [requestType, setRequestType] = useState('article_15');
            const [dueDays, setDueDays] = useState('30');
            const [busy, setBusy] = useState(false);
            const [err, setErr] = useState(null);
            const [copied, setCopied] = useState(false);

            const broker = AEA_BROKERS[brokerIdx];
            const isCustom = broker && broker.contact === '' ;
            const brokerName = isCustom ? customName : broker.name;
            const brokerContact = isCustom ? customContact : broker.contact;
            const rt = AEA_REQUEST_TYPES.find(t => t.value === requestType);

            const letter = aeaDsarLetter({
                broker: brokerName, contact: brokerContact,
                subjectName, requestType, maidTail,
            });

            async function copyLetter() {
                try {
                    await navigator.clipboard.writeText(letter);
                    setCopied(true); setTimeout(() => setCopied(false), 2000);
                } catch (_) { setErr('Copy failed — select the text and copy manually.'); }
            }

            async function submit() {
                if (!brokerName.trim()) { setErr('Choose or name a broker.'); return; }
                setBusy(true); setErr(null);
                try {
                    const body = {
                        broker_name: brokerName.trim(),
                        request_type: requestType,
                        broker_contact: brokerContact.trim() || null,
                        sent_at: new Date().toISOString(),
                    };
                    const days = parseInt(dueDays, 10);
                    if (days > 0) {
                        const d = new Date(); d.setDate(d.getDate() + days);
                        body.response_due_at = d.toISOString();
                    }
                    await api(`/api/v1/aea/devices/${deviceId}/broker-requests`,
                        { method: 'POST', body: JSON.stringify(body) }, token);
                    toast(`DSAR logged — ${brokerName.trim()}`);
                    if (onDone) onDone();
                } catch (ex) { setErr((ex.data && ex.data.detail) || ex.message); }
                finally { setBusy(false); }
            }

            const inputStyle = { width: '100%', background: 'var(--zone-2)', color: 'var(--text)', border: '1px solid var(--veil)', padding: '10px 12px', fontSize: '14px' };

            return (
                <Dialog onClose={onClose} title="Log DSAR request" wide>
                    <div className="text-sm text-muted mb-5 leading-relaxed">
                        Pick a broker and request type, <b>copy the letter</b> and email it to their privacy contact with
                        the subject's signed authority attached — then log it here so the deadline is tracked.
                    </div>
                    <div className="flex gap-6" style={{ flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 280px', minWidth: 260 }} className="space-y-4">
                            <div>
                                <label className="font-mono text-xs uppercase tracking-widest text-muted">Broker</label>
                                <select value={brokerIdx} onChange={e => setBrokerIdx(parseInt(e.target.value, 10))} style={{ ...inputStyle, marginTop: 6 }}>
                                    {AEA_BROKERS.map((b, i) => <option key={i} value={i}>{b.name}</option>)}
                                </select>
                                {broker && broker.note && !isCustom &&
                                    <div className="text-xs text-muted mt-2 leading-relaxed">{broker.note}</div>}
                            </div>
                            {isCustom && (
                                <>
                                    <div>
                                        <label className="font-mono text-xs uppercase tracking-widest text-muted">Broker name *</label>
                                        <input value={customName} onChange={e => setCustomName(e.target.value)} style={{ ...inputStyle, marginTop: 6 }} />
                                    </div>
                                    <div>
                                        <label className="font-mono text-xs uppercase tracking-widest text-muted">Privacy contact</label>
                                        <input value={customContact} onChange={e => setCustomContact(e.target.value)} placeholder="privacy@…" style={{ ...inputStyle, marginTop: 6 }} />
                                    </div>
                                </>
                            )}
                            <div>
                                <label className="font-mono text-xs uppercase tracking-widest text-muted">Request type</label>
                                <select value={requestType} onChange={e => setRequestType(e.target.value)} style={{ ...inputStyle, marginTop: 6 }}>
                                    {AEA_REQUEST_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                                {rt && <div className="text-xs text-muted mt-2 leading-relaxed">{rt.blurb}</div>}
                            </div>
                            <div>
                                <label className="font-mono text-xs uppercase tracking-widest text-muted">Response due in</label>
                                <select value={dueDays} onChange={e => setDueDays(e.target.value)} style={{ ...inputStyle, marginTop: 6 }}>
                                    <option value="30">30 days (UK/EU statutory)</option>
                                    <option value="45">45 days (CCPA)</option>
                                </select>
                            </div>
                        </div>
                        <div style={{ flex: '1 1 320px', minWidth: 280 }}>
                            <div className="flex items-center justify-between mb-2">
                                <label className="font-mono text-xs uppercase tracking-widest text-muted">Letter to send</label>
                                <button type="button" className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={copyLetter}>
                                    {copied ? '✓ Copied' : 'Copy'}
                                </button>
                            </div>
                            <textarea readOnly value={letter}
                                      style={{ ...inputStyle, height: 300, fontFamily: 'var(--font-mono, monospace)', fontSize: 11, lineHeight: 1.5, resize: 'vertical' }} />
                            <div className="text-xs text-muted mt-2">Attach the signed authority. Confirm the broker's current privacy address before sending.</div>
                        </div>
                    </div>
                    {err && <div className="text-sm mt-4" style={{ color: 'var(--red)' }}>{err}</div>}
                    <div className="flex justify-end gap-3 pt-5">
                        <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                        <button type="button" className="btn-gold" disabled={busy || !brokerName.trim()} onClick={submit}>
                            {busy ? 'Logging…' : 'Log request as sent'}
                        </button>
                    </div>
                </Dialog>
            );
        }

export { AeaBrokerRequestDialog };
